import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Button, 
  Chip, 
  Stack, 
  Snackbar, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Deck as BalconyIcon,
  SquareFoot as SquareFootIcon,
  AspectRatio as AspectRatioIcon,
  Explore as ExploreIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Receipt as ReceiptIcon,
  AddCircle as AddCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Inventory2 as Inventory2Icon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { getBookingStatusProps, formatCurrencyWithLabel } from '../../../utils/helper';
import { bookingTokens, cardSx, cardHeadSx, statBoxSx, statBoxAccentSx, tableHeadSx, emptyStateSx, primaryButtonSx, ghostButtonSx } from './bookingDetailsTheme';
import EditBookingDetails from './EditBookingDetails';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchOtherChargesSelect, fetchBookingStatusSelect, fetchBookingDocumentTypeSelect, fetchPaymentModeSelect, fetchPaymentStatusSelect } from '../../../services/common';
import AddBookingDocument from './AddBookingDocument';
import AddBookingFinance from '../AddBookingFinance';
import {generateFormatedLink} from '../../../utils/helper';
import AddBookingPaymentDialog from './AddBookingPaymentDialog';
import PhoneInput from '../../../components/common/PhoneInput';
import EditCommissionDialog from './EditCommissionDialog';
import AdditionalCustomers from './AdditionalCustomers';  

import {
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';

const STATUS_ORDER = [
  'draft',
  'pending',
  'reserved',
  'confirmed',
  'completed',
  'cancelled',
  'expired'
];

const STATUS_FLOW = { 
  draft: {
    next: ['pending', 'reserved', 'cancelled'],
    docRequired: {
      pending: false,
      reserved: false,
      cancelled: false,
    }
  },
  pending: {
    next: ['reserved', 'cancelled'],
    docRequired: {
      reserved: true, // e.g. Reservation Form required
      cancelled: false,
    },
    docType: {
      reserved: 'Reservation Form'
    }
  },
  reserved: {
    next: ['confirmed', 'cancelled', 'expired'],
    docRequired: {
      confirmed: true, // e.g. Payment Proof required
      cancelled: false,
      expired: false,
    },
    docType: {
      confirmed: 'Payment Proof'
    }
  },
  confirmed: {
    next: ['completed', 'cancelled'],
    docRequired: {
      completed: true, // e.g. Sale Agreement required
      cancelled: false,
    },
    docType: {
      completed: 'Sale Agreement'
    }
  },
  completed: {
    next: [],
    docRequired: {}
  },
  cancelled: {
    next: [],
    docRequired: {}
  },
  expired: {
    next: [],
    docRequired: {}
  }
};

const getAllowedNextStatuses = (currentStatus) => {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  if (currentIndex === -1) return [];

  let allowed = STATUS_ORDER.slice(currentIndex + 1);

  if (
    !['cancelled', 'completed', 'expired'].includes(currentStatus) &&
    !allowed.includes('cancelled')
  ) {
    allowed.push('cancelled');
  }

  allowed = [...new Set(allowed)];
  return allowed;
};

const BookingOverviewTab = ({ booking, onActionComplete, onOpenAddUnitDialog }) => {
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { openSuccessModal, openErrorModal, openWarningModal, openConfirmModal } = useModal();
  const { updateBooking, updateAdditionalCustomer, updateBookingStatus, updatePrimaryCustomer, updateBookingCommission } = useBookingDetails();
  const [otherChargesSelect, setOtherChargesSelect] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState([]);
  const [documentTypeSelect, setDocumentTypeSelect] = useState([]);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [localStatus, setLocalStatus] = useState(booking.status);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [otherChargesAnchor, setOtherChargesAnchor] = useState(null);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [editable, setEditable] = useState(booking.status === 'draft' ? true : false);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [customerForm, setCustomerForm] = useState({ customer_name: '', customer_email: '', customer_phone: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [additionalCustomers, setAdditionalCustomers] = useState(booking.additional_customers || []);
  const [editFinanceDialogOpen, setEditFinanceDialogOpen] = useState(false);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [customerFormSubmitted, setCustomerFormSubmitted] = useState(false);
  const [editingPrimaryCustomer, setEditingPrimaryCustomer] = useState(false);
  const [primaryCustomerForm, setPrimaryCustomerForm] = useState({ customer_name: '', customer_email: '', customer_phone: '' });
  const [otherIncentivesAnchor, setOtherIncentivesAnchor] = useState(null);
  const [editCommissionDialogOpen, setEditCommissionDialogOpen] = useState(false);

  useEffect(() => {
    const fetchOtherCharges = async () => {
      try {
        const response = await fetchOtherChargesSelect();
        setOtherChargesSelect(response);
      } catch (error) {
        console.error('Error fetching other charges:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchBookingStatus = async () => {
      try {
        const response = await fetchBookingStatusSelect();
        setStatusOptions(response);
      } catch (error) {
        console.error('Error fetching booking status:', error);
      }
    };

    const fetchBookingDocumentType = async () => {
      try {
        const response = await fetchBookingDocumentTypeSelect();
        setDocumentTypeSelect(response);
      } catch (error) {
        console.error('Error fetching booking document type:', error);
      }
    };

    const fetchPaymentModes = async () => {
      try {
        const response = await fetchPaymentModeSelect();
        setPaymentModes(response);
      } catch (error) {
        console.error('Error fetching payment modes:', error);
      }
    };

    const fetchPaymentStatuses = async () => {
      try {
        const response = await fetchPaymentStatusSelect();
        setPaymentStatuses(response);
      } catch (error) {
        console.error('Error fetching payment statuses:', error);
      }
    };

    fetchOtherCharges();
    fetchBookingStatus();
    fetchBookingDocumentType();
    fetchPaymentModes();
    fetchPaymentStatuses();
  }, []);

  useEffect(() => {
    setLocalStatus(booking.status);
  }, [booking.status]);

  useEffect(() => {
    setAdditionalCustomers(booking.additional_customers || []);
  }, [booking.additional_customers]);

  useEffect(() => {
    const fetchOtherCharges = async () => {
      try {
        const response = await fetchOtherChargesSelect();
        setOtherChargesSelect(response);
      } catch (error) {
        console.error('Error fetching other charges:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchBookingStatus = async () => {
      try {
        const response = await fetchBookingStatusSelect();
        setStatusOptions(response);
      } catch (error) {
        console.error('Error fetching booking status:', error);
      }
    };

    const fetchBookingDocumentType = async () => {
      try {
        const response = await fetchBookingDocumentTypeSelect();
        setDocumentTypeSelect(response);
      } catch (error) {
        console.error('Error fetching booking document type:', error);
      }
    };

    const fetchPaymentModes = async () => {
      try {
        const response = await fetchPaymentModeSelect();
        setPaymentModes(response);
      } catch (error) {
        console.error('Error fetching payment modes:', error);
      }
    };

    const fetchPaymentStatuses = async () => {
      try {
        const response = await fetchPaymentStatusSelect();
        setPaymentStatuses(response);
      } catch (error) {
        console.error('Error fetching payment statuses:', error);
      }
    };

    fetchOtherCharges();
    fetchBookingStatus();
    fetchBookingDocumentType();
    fetchPaymentModes();
    fetchPaymentStatuses();
  }, []);

  useEffect(() => {
    setLocalStatus(booking.status);
  }, [booking.status]);

  useEffect(() => {
    setAdditionalCustomers(booking.additional_customers || []);
  }, [booking.additional_customers]);

  const {
    customer,
    unit,
    project,
    status,
    status_verbose,
    booking_date,
    sale_order,
    payment_breakdown,
    agent,
    commission_breakdown
  } = booking;



  // Financial calculations
  const totalPrice = Number(sale_order?.total_price || 0);
  const basePrice = Number(sale_order?.base_price || 0);
  const taxes = Number(sale_order?.taxes || 0);
  const totalOtherCharges = Number(sale_order?.total_other_charges || 0);
  
  // Calculate pending amount (total price minus any payments made)
  const totalPaid = Number(booking?.payment_breakdown?.total_paid || booking?.total_paid || 0);
  const pendingAmount = Math.max(0, totalPrice - totalPaid);

  // Status chip
  const statusProps = getBookingStatusProps(localStatus, status_verbose);

  // Handle actions
  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleUpdateFinanceClick = () => {
    const hasUnit = booking?.unit != null;
    if (!hasUnit) {
      openConfirmModal(
        'Attach unit first',
        'Please attach a project/unit to this booking before updating finance. Would you like to open the unit selector?',
        () => {
          onOpenAddUnitDialog?.();
        },
        'Open unit selector'
      );
      return;
    }
    setEditFinanceDialogOpen(true);
  };

  const handleEditClose = (result) => {
    setEditDialogOpen(false);
    if (result === 'updated') {
      openSuccessModal('Booking updated successfully!');
    }
  };

  const handleUpdate = async (updatedBooking) => {
    try {
      await updateBooking(booking.booking_id, updatedBooking);
      openSuccessModal('Booking updated successfully!');
    } catch (error) {
      console.error('Error updating booking:', error);
      openErrorModal('Failed to update booking');
    }
  };

  const handleStatusChange = (newStatus) => {
    const docRequired = STATUS_FLOW[localStatus]?.docRequired?.[newStatus];
    if (docRequired) {
      setPendingStatus(newStatus);
      setAddDocOpen(true);
    } else {
      updateBookingStatus(booking.id, { status: newStatus });
    }
  };

  const allowedNextStatuses = getAllowedNextStatuses(localStatus);
  const filteredStatusOptions = statusOptions.filter(opt =>
    allowedNextStatuses.includes(opt.value)
  );

  const handleOtherChargesClick = (event) => {
    setOtherChargesAnchor(event.currentTarget);
  };

  const handleOtherChargesClose = () => {
    setOtherChargesAnchor(null);
  };

  const handleOtherIncentivesClick = (event) => {
    setOtherIncentivesAnchor(event.currentTarget);
  };

  const handleOtherIncentivesClose = () => {
    setOtherIncentivesAnchor(null);
  };

  const handleCancel = () => {
    setAddingCustomer(false);
    setEditingCustomerId(null);
    setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
  };

  const handleSaveFinance = async (updatedFinanceData) => {
    try {
      // Close the dialog
      setEditFinanceDialogOpen(false);
      
      // Show success message
      openSuccessModal('Finance details updated successfully!');
      
      // Trigger booking details refresh
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error updating finance:', error);
      openErrorModal('Failed to update finance details');
    }
  };

  const handleAddPaymentClose = () => {
    setAddPaymentDialogOpen(false);
    // Trigger booking details refresh if needed
    if (onActionComplete) {
      onActionComplete();
    }
  };

  const handleCommissionSave = async (updatedCommissionData) => {
    try {
      // Get the commission ID from the commission breakdown
      const commissionId = commission_breakdown?.id;
      if (!commissionId) {
        openErrorModal('Commission not found for this booking');
        return;
      }

      // Prepare the data for the API
      const apiData = {
        commission_amount: updatedCommissionData.commission_amount,
        other_commission: updatedCommissionData.other_incentives,
        base_commission_rate: updatedCommissionData.base_commission_rate
      };

      // Use the existing context function to update commission
      await updateBookingCommission(booking.id, commissionId, apiData);

      // Close the dialog
      setEditCommissionDialogOpen(false);
      
      // Show success message
      openSuccessModal('Commission updated successfully!');
      
      // Trigger booking details refresh
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error updating commission:', error);
      openErrorModal(error.message || 'Failed to update commission');
    }
  };

  const handleCustomerFormSubmit = async (e) => {
    e.preventDefault();

    
    setCustomerFormSubmitted(true);
    if (!customerForm.customer_name.trim()) {
      openErrorModal('Name is required');
      return;
    }

    // Check for duplicate customer names
    const trimmedName = customerForm.customer_name.trim().toLowerCase();
    const isDuplicate = additionalCustomers.some(customer => {
      const existingName = (customer.name || '').toLowerCase();
      // Skip checking against the customer being edited
      if (editingCustomerId && customer.id === editingCustomerId) {
        return false;
      }
      return existingName === trimmedName;
    });

    if (isDuplicate) {
      openErrorModal('A customer with this name already exists');
      return;
    }

    setFormLoading(true);
    try {
      let updatedCustomers;
      if (editingCustomerId) {
        // Update existing customer
        const currentCustomer = additionalCustomers.find(c => c.id === editingCustomerId);
        
        
        updatedCustomers = additionalCustomers.map(c =>
          c.id === editingCustomerId ? { 
            ...c, 
            name: customerForm.customer_name.trim(),
            email: customerForm.customer_email.trim(),
            phone: customerForm.customer_phone.trim()
          } : c
        );
      } else {
        // Add new customer
        updatedCustomers = [
          ...additionalCustomers,
          { 
            name: customerForm.customer_name.trim(),
            email: customerForm.customer_email.trim(),
            phone: customerForm.customer_phone.trim(),
            id: Date.now() 
          }
        ];
      }
      
      const result = await updateAdditionalCustomer(booking.id, updatedCustomers);
      setAdditionalCustomers(updatedCustomers);
      setAddingCustomer(false);
      setEditingCustomerId(null);
      setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
      setCustomerFormSubmitted(false);
      openSuccessModal('Additional customers updated!');
    } catch (err) {
      console.error('Error updating customers:', err);
      openErrorModal('Failed to update additional customers');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOtherCustomer = async (customerId) => {
    setFormLoading(true);
    try {
      const updatedCustomers = additionalCustomers.filter(c => c.id !== customerId);
      await updateAdditionalCustomer(booking.id, updatedCustomers);
      setAdditionalCustomers(updatedCustomers);
      setCustomerFormSubmitted(false);
      openSuccessModal('Additional customers updated!');
    } catch (err) {
      openErrorModal('Failed to update additional customers');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddOtherCustomer = () => {
    if ((booking.additional_customers || []).length >= 5) {
      openWarningModal('Not Allowed!', 'Maximum 5 additional customers allowed.');
      return;
    }
    setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
    setAddingCustomer(true);
    setEditingCustomerId(null);
    setCustomerFormSubmitted(false);
  };

  const handleEditOtherCustomer = (customer) => {
    setCustomerForm({ 
      customer_name: customer.name, 
      customer_email: customer.email, 
      customer_phone: customer.phone 
    });
    setEditingCustomerId(customer.id);
    setAddingCustomer(false);
    setCustomerFormSubmitted(false);
  };

  const handleCustomerFormChange = (e) => {
    setCustomerForm({ ...customerForm, [e.target.name]: e.target.value });
  };

  // Primary customer editing handlers
  const handleEditPrimaryCustomer = () => {
    setPrimaryCustomerForm({
      customer_name: customer?.customer_name || '',
      customer_email: customer?.customer_email || '',
      customer_phone: customer?.customer_phone || ''
    });
    setEditingPrimaryCustomer(true);
  };

  const handlePrimaryCustomerFormChange = (e) => {
    setPrimaryCustomerForm({ ...primaryCustomerForm, [e.target.name]: e.target.value });
  };

  const handlePrimaryCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!primaryCustomerForm.customer_name.trim()) {
      openErrorModal('Customer name is required');
      return;
    }

    setFormLoading(true);
    try {
      // Update primary customer using the dedicated function
      const customerData = {
        customer_name: primaryCustomerForm.customer_name.trim(),
        customer_email: primaryCustomerForm.customer_email.trim(),
        customer_phone: primaryCustomerForm.customer_phone.trim()
      };
      
      await updatePrimaryCustomer(booking.id, customerData);
      setEditingPrimaryCustomer(false);
      openSuccessModal('Primary customer updated successfully!');
      
      // Trigger refresh if callback provided
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (err) {
      console.error('Error updating primary customer:', err);
      openErrorModal('Failed to update primary customer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelPrimaryCustomer = () => {
    setEditingPrimaryCustomer(false);
    setPrimaryCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
  };

  // Safety check for booking data - must be after all hooks
  if (!booking) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Booking data not available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Booking Summary and Commission Summary Cards - reference design */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ ...cardSx, height: '100%' }}>
            <Box sx={cardHeadSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ fontSize: 14 }}>📋</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink, letterSpacing: '-0.2px' }}>Booking Summary</Typography>
              </Box>
              {(booking.status === 'draft' || booking.status === 'pending') && (
                <Button size="small" onClick={handleUpdateFinanceClick} sx={{ ...primaryButtonSx, py: '6px', px: 1.5, fontSize: 12 }} startIcon={<Typography component="span" sx={{ fontSize: 12 }}>₹</Typography>}>Update Finance</Button>
              )}
            </Box>
            <Box sx={{ p: '16px 18px', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Grid container spacing={1.5}>
               {/* Commission Rate */}
               <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  &nbsp;
                </Typography>
              </Grid>

              {/* Reservation */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <ReceiptIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Booking Amount
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(sale_order?.booking_amount || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Advance */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <AddCircleIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Token Amount
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(sale_order?.token_amount || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Paid Till Date */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Paid Till Date
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(payment_breakdown?.total_paid || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Balance */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <CurrencyRupeeIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Balance
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(payment_breakdown?.remaining_amount || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Next Due Payment - Highlighted */}
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1.5,
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Next Due Payment
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    {payment_breakdown?.next_payment_due || 'No payment scheduled'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Commission Summary Card */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ ...cardSx, height: '100%' }}>
            <Box sx={{ ...cardHeadSx }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ fontSize: 14 }}>💰</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink, letterSpacing: '-0.2px' }}>Commission Summary</Typography>
              </Box>
              {commission_breakdown?.is_editable && (
                <Button size="small" onClick={() => setEditCommissionDialogOpen(true)} sx={ghostButtonSx}>Edit</Button>
              )}
            </Box>
            <Box sx={{ p: '16px 18px' }}>
            <Typography variant="caption" sx={{ fontSize: 11.5, color: bookingTokens.muted, fontWeight: 500 }}>Rate: <strong style={{ color: bookingTokens.green }}>{commission_breakdown?.base_commission_rate}% of Base Price</strong></Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>

              {/* Commission Amount */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <ReceiptIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      {commission_breakdown?.commission_type === 'estimated' ? 'Estimated Commission' : 'Total Commission'}
                    </Typography>
                    <Tooltip title="Including base commission + other incentives" arrow>
                      <InfoIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(commission_breakdown?.total_commission || 0)}
                  </Typography>
                  
                </Box>
              </Grid>

              {/* Other Incentives */}
              <Grid item xs={6}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.200'
                    }
                  }}
                  onClick={handleOtherIncentivesClick}
                >
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Other Incentives
                    </Typography>
                    <Tooltip title="Click to view breakdown" arrow>
                      <InfoIcon 
                        sx={{ fontSize: 12, color: 'text.secondary', cursor: 'pointer' }} 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOtherIncentivesClick(e);
                        }}
                      />
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(commission_breakdown?.total_other_commission || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Received */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Received
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(commission_breakdown?.total_commission_received || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Pending */}
              <Grid item xs={6}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 14, color: 'text.primary' }} />
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      Pending
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {formatCurrencyWithLabel(commission_breakdown?.total_commission_pending || 0)}
                  </Typography>
                </Box>
              </Grid>

              {/* Next Payout - Highlighted */}
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1.5,
                  textAlign: 'center'
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <PaymentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Next Payout
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    {agent?.next_commission_payment || 'No payment scheduled'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Customers - redesigned to match green design system */}
      <AdditionalCustomers
        booking={booking}
        onUpdate={onActionComplete}
      />


      {/* Edit Dialog */}
      <EditBookingDetails
        open={editDialogOpen}
        onClose={handleEditClose}
        booking={booking}
        otherChargesSelect={otherChargesSelect}
      />

      <AddBookingDocument
        open={addDocOpen}
        onClose={() => {
          setAddDocOpen(false);
          setPendingStatus(null);
        }}
        bookingId={booking.id}
        documentTypeOptions={documentTypeSelect}
      />

      <Popover
        open={Boolean(otherChargesAnchor)}
        anchorEl={otherChargesAnchor}
        onClose={handleOtherChargesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Other Charges Breakup</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sale_order?.other_charges || []).map((charge) => (
                <TableRow key={charge.name}>
                  <TableCell sx={{ fontSize: '0.875rem' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.5rem' }}>{charge.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{charge.description}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    ₹{Number(charge.amount).toLocaleString('en-IN')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Popover>

      <AddBookingFinance
        open={editFinanceDialogOpen}
        onClose={() => setEditFinanceDialogOpen(false)}
        booking={booking}
        onSave={handleSaveFinance}
        isEdit={true}
        otherCharges={otherChargesSelect}
      />

      <AddBookingPaymentDialog
        open={addPaymentDialogOpen}
        onClose={handleAddPaymentClose}
        booking={booking}
        pendingAmount={pendingAmount}
        paymentModes={paymentModes}
        paymentStatuses={paymentStatuses} 
      />

      <EditCommissionDialog
        open={editCommissionDialogOpen}
        onClose={() => setEditCommissionDialogOpen(false)}
        booking={booking}
        onSave={handleCommissionSave}
      />

      {/* Other Incentives Popover */}
      <Popover
        open={Boolean(otherIncentivesAnchor)}
        anchorEl={otherIncentivesAnchor}
        onClose={handleOtherIncentivesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Other Incentives Breakdown</Typography>
          {commission_breakdown?.other_commission && commission_breakdown.other_commission.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Incentive Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commission_breakdown.other_commission.map((incentive, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{incentive.name || 'Incentive'}</Typography>
                      {incentive.description && (
                        <Typography variant="caption" color="text.secondary">{incentive.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      ₹{Number(incentive.amount || 0).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No other incentives added yet.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default BookingOverviewTab;
